import { CodeEditor } from '@datacamp/code-editor';
import first from 'lodash/first';
import uniqueIdFn from 'lodash/uniqueId';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useUnmount } from 'react-use';

import { useDispatch, useSelector } from '../../interfaces/State';
import type { SaveCodeArgs } from '../../redux/actions';
import { showHint, showSolution } from '../../redux/actions';
import {
  selectCurrentSubExercise,
  selectExercise,
  selectHintOrSolution,
} from '../../redux/selectors';
import ShowReconnectorIfRequired from '../ShowReconnectorIfRequired';
import ThemeToggler from '../ThemeToggler';

import CodeEditorControlButtons from './CodeEditorControlButtons';
import getHighlightLocation from './highlight';
import useAutoCompletionProviders from './useAutoCompletionProviders';

// Most of the languages are normalized using the @datacamp/dc-language-config package,
// this function handles some rare exceptions
const mapToSupportedLanguage = (language: any, exerciseType: any) => {
  if (exerciseType === 'RCppExercise') {
    return 'cpp';
  }
  if (language === 'revo') {
    return 'r';
  }
  if (language === 'containers') {
    return 'shell';
  }
  return language;
};

// ignore multiple choice user's submission
const formatCode = (code: any) => {
  const selectedOption = first((code || '').match(/selected_option = [0-9]*/g));
  return selectedOption === code ? '' : code;
};

type Props = {
  active?: boolean;
  buildCompleter: (...args: any[]) => any;
  canExplainSolution: boolean;
  canRun: boolean;
  canSubmit: boolean;
  ckey: string;
  code?: string;
  disableExecution: boolean;
  glContainer?: {
    layoutManager?: any;
  };
  hideOpenAIBranding: boolean;
  id: number;
  isMarkdownExercise?: boolean;
  isSolution?: boolean;
  language: string;
  lineErrors?: unknown;
  markdown?: {
    activeTitle?: string;
  };
  nbAttempts?: number;
  onCodeChange?: (...args: any[]) => any;
  onExplainSolution: (...args: any[]) => any;
  onFocusChanged?: (...args: any[]) => any;
  onRunCode: (...args: any[]) => any;
  onSubmitCode: (...args: any[]) => any;
  onSubmitShortcut: (...args: any[]) => any;
  pre_exercise_code?: string;
  readOnly?: boolean;
  resetCode?: string;
  saveCode: (props: SaveCodeArgs) => any;
  sct?: string;
  shouldFocus?: boolean;
  solution?: string;
  toggleUITheme: (...args: any[]) => any;
  type?: string;
  uiTheme: 'DARK' | 'LIGHT';
  uniqueId?: string;
  updateAttempts: (...args: any[]) => any;
  updateConsoleCode: (...args: any[]) => any;
};

const MonacoCodeEditor: React.FC<Props> = ({
  active,
  canExplainSolution,
  canRun,
  canSubmit,
  ckey,
  code,
  disableExecution,
  hideOpenAIBranding,
  id,
  isMarkdownExercise,
  isSolution,
  language,
  lineErrors,
  markdown,
  nbAttempts,
  onExplainSolution,
  onRunCode,
  onSubmitCode,
  onSubmitShortcut,
  pre_exercise_code: preExerciseCode,
  resetCode,
  saveCode,
  sct,
  solution,
  toggleUITheme,
  type,
  uiTheme,
  uniqueId,
  updateAttempts,
  updateConsoleCode,
}) => {
  const [currentCode, setCurrentCode] = useState(() => formatCode(code));
  const [selectedCode, setSelectedCode] = useState(() => formatCode(''));
  const [currentLineValue, setCurrentLineValue] = useState<string>();
  const editorRef = useRef(null);
  const editorName = useRef(uniqueId || uniqueIdFn('code-editor-'));
  const focus = useCallback(() => {
    if (editorRef.current != null) {
      // @ts-expect-error ts-migrate(2531) FIXME: Object is possibly 'null'.
      editorRef.current.focus();
    }
  }, []);

  useEffect(() => {
    focus();
  }, [focus]);

  useEffect(() => {
    const formattedCode = formatCode(code);
    setCurrentCode(formattedCode);
  }, [code]);

  const [currentHighlight, setCurrentHighlight] = useState(lineErrors);
  useEffect(() => {
    setCurrentHighlight(lineErrors);
  }, [lineErrors]);

  const onValueChange = useCallback((newCode) => {
    setCurrentCode(newCode);
    setCurrentHighlight(undefined);
  }, []);

  const onCurrentLineChange = useCallback(({ lineValue }) => {
    setCurrentLineValue(lineValue);
  }, []);

  const highlightLocation = useMemo(
    () => getHighlightLocation(currentHighlight),
    [currentHighlight],
  );

  const autoCompletionProviders = useAutoCompletionProviders({
    pec: preExerciseCode,
    sct,
    solution,
    type,
    language,
  });

  const runCode = useCallback(
    ({ shouldMoveCursorToNextLine }) => {
      onRunCode();
      if (!shouldMoveCursorToNextLine) {
        return;
      }
      const editor = editorRef.current;
      if (editor == null) {
        return;
      }
      // @ts-expect-error ts-migrate(2339) FIXME: Property 'moveCursorDown' does not exist on type '... Remove this comment to see the full error message
      editor.moveCursorDown({ numberOfLines: 1 });
    },
    [onRunCode],
  );

  // If we save the code on every key press, we would end up in an infinite loop
  // because the code can be updated from the state as well (above effect).
  useUnmount(() => {
    saveCode({
      id,
      code: currentCode,
      tabKey: ckey,
      category: 'editorTabs',
    });
  });

  const hintAndSolution = useSelector(selectHintOrSolution);
  const exerciseId = useSelector(selectExercise).get('id');
  const isSolutionShown = useSelector(selectExercise).getIn(
    ['user', 'isSolutionShown'],
    false,
  );
  const subExerciseId = useSelector(selectCurrentSubExercise).get('id');
  const currentExerciseId = subExerciseId != null ? subExerciseId : exerciseId;
  const { xp } = hintAndSolution;

  const dispatch = useDispatch();

  const onTakeHintOrSolution = useCallback(() => {
    if (!hintAndSolution.isSolution) {
      dispatch(showHint(currentExerciseId, xp));
    } else {
      if (isSolutionShown) {
        return;
      }
      dispatch(showSolution(language, currentExerciseId, xp));
    }
  }, [
    currentExerciseId,
    dispatch,
    hintAndSolution.isSolution,
    isSolutionShown,
    language,
    xp,
  ]);

  return (
    <>
      <ThemeToggler toggleUITheme={toggleUITheme} uiTheme={uiTheme} />
      <div className="code-editor" data-cy="code-editor">
        <ShowReconnectorIfRequired isMarkdownExercise={isMarkdownExercise} />
        <CodeEditor
          autoCompletionProviders={autoCompletionProviders}
          ref={editorRef}
          editorName={editorName.current}
          onValueChange={onValueChange}
          language={mapToSupportedLanguage(language, type)}
          value={currentCode}
          theme={uiTheme === 'LIGHT' ? 'light' : 'dark'}
          highlightLocation={highlightLocation}
          onSelectedCodeChange={setSelectedCode}
          onTakeHintOrSolution={onTakeHintOrSolution}
          onCurrentLineChange={onCurrentLineChange}
        />
        <CodeEditorControlButtons
          canExplainSolution={canExplainSolution}
          onExplainSolution={onExplainSolution}
          canSubmit={canSubmit}
          id={id}
          ckey={ckey}
          canRun={canRun}
          type={type}
          resetCode={resetCode}
          onRunCode={runCode}
          code={currentCode}
          currentLineValue={currentLineValue}
          language={language}
          updateConsoleCode={updateConsoleCode}
          solution={solution}
          preExerciseCode={preExerciseCode}
          sct={sct}
          isSolution={isSolution}
          isMarkdownExercise={isMarkdownExercise}
          nbAttempts={nbAttempts}
          updateAttempts={updateAttempts}
          onSubmitCode={onSubmitCode}
          markdown={markdown}
          onSubmitShortcut={onSubmitShortcut}
          setCurrentCode={setCurrentCode}
          active={active}
          focus={focus}
          selectedCode={selectedCode}
          disableExecution={disableExecution}
          hideOpenAIBranding={hideOpenAIBranding}
        />
      </div>
    </>
  );
};

export default MonacoCodeEditor;
